export const SKILL_BLOCK_TYPES = {
  Skill: 'skill',
  Form: 'form',
} as const

export type SkillBlockType = (typeof SKILL_BLOCK_TYPES)[keyof typeof SKILL_BLOCK_TYPES]

export interface SkillBlockItem {
  type: SkillBlockType
  path: string
  formId?: string
  formTitle?: string
}

export type SkillBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'skillBlock'
      skill: SkillBlockItem
    }

const SKILL_BLOCK_PATTERN = /(?:\\*\[){2}(?:\\*\/)skills(?:\\*\/).*?(?:\\*\]){2}/g
const SKILL_BLOCK_HTML_PATTERN =
  /<skill-block\b([^>]*)>\s*<\/skill-block>|<skill-block\b([^>]*)\/>/g
const SKILL_BLOCK_PLACEHOLDER = 'KARAKURI_SKILL_BLOCK'

export const createDefaultSkillBlockItem = (): SkillBlockItem => ({
  type: SKILL_BLOCK_TYPES.Skill,
  path: '/skills/',
})

export function createSkillBlockItemFromPath(path: string, formTitle?: string): SkillBlockItem {
  return normalizeSkillBlockItem({ path, formTitle })
}

export function normalizeSkillBlockItem(item: Partial<SkillBlockItem>): SkillBlockItem {
  const path = normalizeSkillPath(item.path ?? '')
  const formId = readSkillPathParam(path, 'form')
  const type = formId ? SKILL_BLOCK_TYPES.Form : SKILL_BLOCK_TYPES.Skill

  return {
    type,
    path,
    formId,
    formTitle: item.formTitle?.trim() ?? '',
  }
}

export function encodeSkillBlockItem(item: Partial<SkillBlockItem>): string {
  return encodeURIComponent(JSON.stringify(normalizeSkillBlockItem(item)))
}

export function decodeSkillBlockItem(encodedItem: string | undefined): SkillBlockItem {
  if (!encodedItem) {
    return createDefaultSkillBlockItem()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItem))
    if (!isSkillBlockItemLike(parsed)) {
      return createDefaultSkillBlockItem()
    }

    return normalizeSkillBlockItem(parsed)
  } catch {
    return createDefaultSkillBlockItem()
  }
}

export function serializeSkillBlockMarkdown(item: Partial<SkillBlockItem>): string {
  const skill = normalizeSkillBlockItem(item)
  return `[[${skill.path}]]`
}

export function splitSkillBlocksFromMarkdown(markdown: string): SkillBlockMarkdownSegment[] {
  const segments: SkillBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(SKILL_BLOCK_PATTERN, (matchedText: string, offset: number) => {
    const skillPath = extractSkillPath(matchedText)
    if (offset > lastIndex) {
      segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
    }
    segments.push({ type: 'skillBlock', skill: createSkillBlockItemFromPath(skillPath) })
    lastIndex = offset + matchedText.length
    return matchedText
  })

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceSkillBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  skills: SkillBlockItem[]
} {
  const skills: SkillBlockItem[] = []
  const replacedMarkdown = markdown.replace(SKILL_BLOCK_PATTERN, matchedText => {
    const index = skills.length
    skills.push(createSkillBlockItemFromPath(extractSkillPath(matchedText)))
    return `${SKILL_BLOCK_PLACEHOLDER}_${index}`
  })

  return { markdown: replacedMarkdown, skills }
}

function extractSkillPath(shortcode: string): string {
  const normalizedShortcode = shortcode.replace(/\\/g, '')
  return normalizedShortcode.slice(2, -2)
}

export function restoreSkillBlockHtmlPlaceholders(html: string, skills: SkillBlockItem[]): string {
  return skills.reduce((restoredHtml, skill, index) => {
    const placeholder = `${SKILL_BLOCK_PLACEHOLDER}_${index}`
    const skillBlockHtml = createSkillBlockHtml(skill)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), skillBlockHtml)
      .replace(new RegExp(placeholder, 'g'), skillBlockHtml)
  }, html)
}

export function replaceSkillBlockHtmlWithPlaceholders(html: string): {
  html: string
  skills: SkillBlockItem[]
} {
  const skills: SkillBlockItem[] = []
  const replacedHtml = html.replace(
    SKILL_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      const index = skills.length
      skills.push(decodeSkillBlockItem(readHtmlAttribute(attributesA ?? attributesB ?? '')))
      return `<p>${SKILL_BLOCK_PLACEHOLDER}_${index}</p>`
    }
  )

  return { html: replacedHtml, skills }
}

export function restoreSkillBlockMarkdownPlaceholders(
  markdown: string,
  skills: SkillBlockItem[]
): string {
  return skills.reduce(
    (restoredMarkdown, skill, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${SKILL_BLOCK_PLACEHOLDER}_${index}`),
        serializeSkillBlockMarkdown(skill)
      ),
    markdown
  )
}

export function normalizeSkillPath(value: string): string {
  const unwrappedValue = value.trim().replace(/^\[\[/, '').replace(/\]\]$/, '').trim()
  if (!unwrappedValue) {
    return '/skills/'
  }

  if (/^https?:\/\//.test(unwrappedValue)) {
    try {
      const url = new URL(unwrappedValue)
      return `${url.pathname}${url.search}`
    } catch {
      return '/skills/'
    }
  }

  if (unwrappedValue.startsWith('/skills/')) {
    return unwrappedValue
  }

  if (unwrappedValue.startsWith('skills/')) {
    return `/${unwrappedValue}`
  }

  return `/skills/${unwrappedValue.replace(/^\/+/, '')}`
}

function createSkillBlockHtml(item: SkillBlockItem): string {
  return `<skill-block data-skill="${escapeHtmlAttribute(
    encodeSkillBlockItem(item)
  )}"></skill-block>`
}

function readSkillPathParam(path: string, name: string): string | undefined {
  try {
    return new URL(path, 'http://localhost').searchParams.get(name) ?? undefined
  } catch {
    return undefined
  }
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-skill=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function isSkillBlockItemLike(value: unknown): value is Partial<SkillBlockItem> {
  return typeof value === 'object' && value !== null
}
